import { datadogRum } from '@datadog/browser-rum'
import { getCity, getCountry, getState } from 'common/Footer/helpers'
import { LogEvent } from 'constants/logEvents'
import { getGeocode } from 'use-places-autocomplete'

export const BROWSER_GEOLOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  /** Reuse a recent fix for returning users so we do not re-query GPS every visit. */
  maximumAge: 5 * 60 * 1000,
}

export type BrowserCoordinates = {
  latitude: number
  longitude: number
}

/**
 * Requests the browser's current position. On first visit this triggers the
 * native permission prompt; when permission is already granted it resolves
 * silently; when denied or unavailable it rejects.
 */
export const getBrowserCoordinates = (): Promise<BrowserCoordinates> => {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new Error('Browser geolocation is unavailable'))
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        })
      },
      (error) => {
        reject(error)
      },
      BROWSER_GEOLOCATION_OPTIONS
    )
  })
}

/**
 * Reverse-geocodes lat/lng into the GoogleLocationResult shape used by
 * provider search (city, state, country via address components).
 */
export const reverseGeocodeCoordinates = async (
  latitude: number,
  longitude: number,
  usedForFeature?: string
): Promise<GoogleLocationResult | null> => {
  datadogRum.addAction(LogEvent.GEOCODE_USED, {
    usedForFeature,
  })

  const results = await getGeocode({
    location: { lat: latitude, lng: longitude },
  })
  const firstResult = results?.[0]

  if (!firstResult) {
    return null
  }

  const city = getCity(firstResult)
  const state = getState(firstResult)
  const country = getCountry(firstResult)
  const mainText = city || state || firstResult.formatted_address
  const secondaryParts = [state, country].filter(Boolean)

  return {
    ...firstResult,
    lat: latitude,
    lng: longitude,
    state,
    main_text: mainText,
    secondary_text: secondaryParts.join(', '),
    main_text_matched_substrings: [],
  }
}

/**
 * True when reverse-geocoded location can drive US provider search.
 */
export const isUsProviderSearchLocation = (
  locationData: GoogleLocationResult | null
): boolean => {
  if (!locationData?.state) {
    return false
  }

  const country = getCountry(locationData)

  return country === 'US'
}
